import Vue from 'vue';
import i18n from '../i18n';

import { fetchQuestionSections, fetchUserData, apiSendUserDataToDB } from '../services/apiService';

import router from '../router';

import { isValidState } from './globe';
import { pppSections } from './trivia';
import { getEmptyUserModel, getCompanyPinsFromUserData, groupCompanyPins } from './user';

const MIN_QUESTION_ANSWERED_IN_TIME_LIMIT = 2;

const store = Vue.observable({
  // app
  // call when you want to install as app
  // https://web.dev/customize-install/#listen_for_beforeinstallprompt
  deferedInstallationPrompt: null,

  // user model
  userModel: null,
  userStats: null,
  userCount: 0,

  uiLanguage: 'en',

  // average values
  companyAverageValue: 0,

  // kpi overall
  minKpiValues: null,
  maxKpiValues: null,
  averageKpiValues: null,

  // feed from API service
  questionSections: null,
  sectionsLoaded: false,

  // trivia
  sectionIndex: 0,
  questionIndex: 0,

  // webgl app
  webGLLoaded: false,
  globeState: null,
  isOverValidLocation: false,

  companyPins: {}, // company data for webgl
  companyPinsGrouped: [], // group pins -> TBR to API service later on

  // global kpis
  globalWarming: 0,
  globalSealevel: 0,
  globalPlastic: 0,
  globalHealth: 0,

  selectedPin: 0,

  isUserPinSelected: false,

  // pppFocus
  pppFocus: '',

  initUserModel() {
    this.userModel = getEmptyUserModel();
  },

  /**
   *
   * @param globeState object with properties 'to' and 'from' OR string containing a valid globe.State
   */
  setGlobeState(globeState) {
    // reset transition
    if (typeof globeState === 'object' && globeState.transitionValue === undefined) {
      globeState.transitionValue = 0.0;
    }

    if (!isValidState(globeState)) {
      return;
    }

    store.globeState = globeState;
  },

  setGlobeTransitionValue(transitionValue) {
    if (typeof this.globeState !== 'object') {
      // eslint-disable-next-line no-console
      console.log('state is not a transition state');
      return;
    }

    Vue.set(store.globeState, 'transitionValue', transitionValue);
  },

  async loadQuestionSections() {
    const processQuestionSections = ({ sections }) => {
      this.questionSections = sections;
      this.sectionsLoaded = true;
      this.calculateKpiMaxMinValues();
    };

    try {
      const result = await fetchQuestionSections(i18n.locale);
      processQuestionSections(result);
    } catch (error) {
      // fall back to static json
      const { default: result } = await import('../services/fallbackData/getQuestionSections.json');
      processQuestionSections(result);
    }
  },

  async loadUserData() {
    const processUserData = ({ userData, userCount }) => {
      this.userStats = userData;
      this.userCount = userCount;
      this.companyPins = getCompanyPinsFromUserData(userData);
      this.companyPinsGrouped = groupCompanyPins(this.companyPins);
      this.companyAverageValue = this.calculateUserAverageValue(this.companyPins);
      this.calculateWorldImpactValues(userData);
    };

    try {
      const result = await fetchUserData(i18n.locale);
      processUserData(result);
    } catch (error) {
      // fall back to static json
      const { default: result } = await import('../services/fallbackData/getUserData.json');
      processUserData(result);
    }
  },

  async sendUserDataToDB() {
    const { userLocation } = this.userModel;
    if (userLocation === undefined || (userLocation.lat === 0 && userLocation.long === 0)) {
      return Promise.reject('NO_LOCATION_SET');
    }

    try {
      const response = await apiSendUserDataToDB({
        userId: this.userModel.userId,
        userLocation: `${userLocation.lat}_${userLocation.long}`,
        companyValue: this.userModel.companyValue,
        companyName: this.userModel.companyName,
        kpiPercentagesMatrix: this.userModel.kpiPercentagesMatrix,
        peoplePercentagesValue: this.userModel.peoplePercentage,
        planetPercentagesValue: this.userModel.planetPercentage,
        profitPercentagesValue: this.userModel.profitPercentage,
      });
      // TODO: [SS2-748] handle success
      // eslint-disable-next-line no-console
      console.log('send to db: success', response);
    } catch (error) {
      // TODO: [SS2-748] handle error
      // eslint-disable-next-line no-console
      console.log('send to db: error', error);
    }
  },

  calculateKpiMaxMinValues() {
    const minKpiCurrentValue = [0, 0, 0, 0, 0, 0, 0, 0, 0];
    const maxKpiCurrentValue = [0, 0, 0, 0, 0, 0, 0, 0, 0];
    this.minKpiValues = [0, 0, 0, 0, 0, 0, 0, 0, 0];
    this.maxKpiValues = [0, 0, 0, 0, 0, 0, 0, 0, 0];

    // section
    for (let i = 0; i < this.questionSections.length; i++) {
      // questions

      for (let j = 0; j < this.questionSections[i].sectionQuestions.length; j++) {
        for (
          let k = 0;
          k < this.questionSections[i].sectionQuestions[j].questionAnswers.length;
          k++
        ) {
          // get kpi values
          const kpiObject = this.questionSections[i].sectionQuestions[j].questionAnswers[k]
            .kpiRating[0];

          let index = 0;
          for (const key of Object.keys(kpiObject)) {
            const kpiValue = parseInt(kpiObject[key]);
            if (index < this.minKpiValues.length) {
              if (k === 0) {
                minKpiCurrentValue[index] = kpiValue;
                maxKpiCurrentValue[index] = kpiValue;
              }
              if (k === 1) {
                if (
                  kpiValue !== minKpiCurrentValue[index] ||
                  kpiValue !== minKpiCurrentValue[index]
                ) {
                  if (kpiValue > minKpiCurrentValue[index]) {
                    maxKpiCurrentValue[index] = kpiValue;
                  } else {
                    minKpiCurrentValue[index] = kpiValue;
                  }
                }
                // add average kpiValues for min and max
                this.minKpiValues[index] += minKpiCurrentValue[index];
                this.maxKpiValues[index] += maxKpiCurrentValue[index];
              }
            }
            index++;
          }
        }
      }
    }

    // console.log('Min Max Values:');
    // console.log(this.minKpiValues);
    // console.log(this.maxKpiValues);
  },

  calculateUserAverageValue(companyPins) {
    const companyPinsArray = Object.values(companyPins);
    const companyValues = companyPinsArray.reduce(
      (values, { companyValue }) => (values += companyValue),
      0
    );

    return companyValues / companyPinsArray.length;
  },

  calculateWorldImpactValues(companyData) {
    this.averageKpiValues = JSON.parse(JSON.stringify(this.userModel.kpiPercentages));

    const companyArray = Object.values(companyData);
    companyArray.forEach(element => {
      this.globalWarming += parseFloat(element.kpiPercentagesMatrix[0]['co2e']);
      this.globalSealevel += parseFloat(element.kpiPercentagesMatrix[0]['energyConsumption']);
      this.globalHealth += parseFloat(
        element.kpiPercentagesMatrix[0]['healthBenefitProgramBudget']
      );
      this.globalPlastic += parseFloat(element.kpiPercentagesMatrix[0]['wastePlastic']);

      this.averageKpiValues['co2'] += parseFloat(element.kpiPercentagesMatrix[0]['co2e']);

      this.averageKpiValues['innovation'] += parseFloat(
        element.kpiPercentagesMatrix[0]['abilityToInnovate']
      );

      this.averageKpiValues['customerSatisfaction'] += parseFloat(
        element.kpiPercentagesMatrix[0]['customerSatisfaction']
      );

      this.averageKpiValues['employeeHappiness'] += parseFloat(
        element.kpiPercentagesMatrix[0]['employeeHappiness']
      );

      this.averageKpiValues['energy'] += parseFloat(
        element.kpiPercentagesMatrix[0]['energyConsumption']
      );

      this.averageKpiValues['healthProgram'] += parseFloat(
        element.kpiPercentagesMatrix[0]['healthBenefitProgramBudget']
      );

      this.averageKpiValues['revenue'] += parseFloat(element.kpiPercentagesMatrix[0]['revenue']);

      this.averageKpiValues['training'] += parseFloat(
        element.kpiPercentagesMatrix[0]['trainingDaysYear']
      );

      this.averageKpiValues['plastic'] += parseFloat(
        element.kpiPercentagesMatrix[0]['wastePlastic']
      );
    });
    this.globalWarming = this.globalWarming / companyArray.length;
    this.globalSealevel = this.globalSealevel / companyArray.length;
    this.globalHealth = this.globalHealth / companyArray.length;
    this.globalPlastic = this.globalPlastic / companyArray.length;

    for (let key in this.averageKpiValues) {
      this.averageKpiValues[key] = this.averageKpiValues[key] / companyArray.length;
    }
  },

  kpiAverageValueGaps() {
    let kpiDiffKpiValues = JSON.parse(JSON.stringify(this.userModel.kpiPercentages));

    for (let key in kpiDiffKpiValues) {
      kpiDiffKpiValues[key] = kpiDiffKpiValues[key] - this.averageKpiValues[key];
    }
    return kpiDiffKpiValues;
  },

  setCompanyName(newCompanyName) {
    if (newCompanyName) store.userModel.companyName = newCompanyName.toUpperCase();
  },

  setUserLocation(locationObject) {
    if (locationObject !== null) {
      this.userModel.userLocation = locationObject;
      this.isOverValidLocation = true;
    } else {
      this.isOverValidLocation = false;
    }
  },

  selectCompanyPin(pin) {
    this.selectedPin = pin;

    // Reset
    this.isUserPinSelected = false;
  },

  setIsUserPinSelected(isUserPinSelected) {
    this.isUserPinSelected = isUserPinSelected;

    // Reset
    this.selectedPin = 0;
  },

  resetTrivia() {
    this.questionIndex = 0;
    this.sectionIndex = 0;
  },

  async triviaGoToNextSection() {
    // the index will be changed after the route change
    await router.replace({
      name: this.sectionIndex === 0 ? 'sectionOverview' : 'dashboardSummary',
    });
    this.questionIndex = 0;
    this.sectionIndex++;
  },

  triviaGoToNextQuestion() {
    this.questionIndex++;
  },

  endQuestionSectionByTime() {
    // TBR -> Maybe add min question count into CMS
    if (this.questionIndex <= MIN_QUESTION_ANSWERED_IN_TIME_LIMIT) {
      router.replace({ name: 'timeoutOverview' });
    } else {
      this.triviaGoToNextSection();
    }
  },

  answerQuestion(answerIndex) {
    if (this.questionIndex < this.questionSections[this.sectionIndex].sectionQuestions.length - 1) {
      // add kpi values to user data model
      let kpiObject = this.questionSections[this.sectionIndex].sectionQuestions[this.questionIndex]
        .questionAnswers[answerIndex].kpiRating[0];

      let index = 0;
      for (const key of Object.keys(kpiObject)) {
        const kpiValue = kpiObject[key];

        if (index < Object.keys(this.userModel.kpiValues).length) {
          this.userModel.kpiValues[Object.keys(this.userModel.kpiValues)[index]] += parseInt(
            kpiValue
          );
        }

        index++;
      }

      this.triviaGoToNextQuestion();
    } else {
      this.triviaGoToNextSection();
    }
  },

  getDataOfSingleUserById(userId) {
    const userModel = this.userStats.find(userStat => userStat.userId === userId.id);

    if (userModel.kpiPercentagesMatrix) {
      // radar chart
      const MIN_CHART_VALUE = 0.3;
      const PPP_SECTION_COUNT = 3;
      const kpiPercentages = userModel.kpiPercentagesMatrix[0];
      const kpiRadarChartData = [];

      for (let i = 0; i <= PPP_SECTION_COUNT; i++) {
        let keyIndex = 0;
        const kpiSection = [];

        for (const key of Object.keys(kpiPercentages)) {
          if (!key.includes('col')) {
            let kpiValue = MIN_CHART_VALUE + parseFloat(userModel.kpiPercentagesMatrix[0][key]);

            if (i === 3 && keyIndex > 2) {
              kpiValue = 0;
            }
            if (i === 2 && (keyIndex < 3 || keyIndex > 5)) {
              kpiValue = 0;
            }
            if (i === 1 && keyIndex < 6) {
              kpiValue = 0;
            }

            const kpiObject = {
              axis: key,
              value: kpiValue,
            };

            kpiSection.push(kpiObject);
            keyIndex++;
          }
        }

        kpiSection.reverse();
        kpiRadarChartData.push(kpiSection);
      }
      userModel.kpiRadarChartData = kpiRadarChartData;
    }

    return userModel;
  },
});

export default store;

export function getCompanyName() {
  return store.userModel.companyName;
}

export function impactDistribution() {
  const getImpact = kpiId =>
    ((store.userModel.kpiPercentages[kpiId] * 100) / store.userCount).toFixed(2);

  return [
    {
      label: i18n.t('kpi.impactDistribution.globalWarming'),
      value: -getImpact('co2'),
      globalValue: 1 - store.globalWarming,
      order: -1,
    },
    {
      label: i18n.t('kpi.impactDistribution.worldHealth'),
      value: getImpact('healthProgram'),
      globalValue: 1 - store.globalHealth,
      order: 1,
    },
    {
      label: i18n.t('kpi.impactDistribution.seaLevelsRising'),
      value: -getImpact('energy'),
      globalValue: 1 - store.globalSealevel,
      order: -1,
    },
    {
      label: i18n.t('kpi.impactDistribution.plasticInOceans'),
      value: -getImpact('plastic'),
      globalValue: 1 - store.globalPlastic,
      order: -1,
    },
  ];
}

export function setWebGlLoaded(webGLLoaded) {
  store.webGLLoaded = webGLLoaded;
}

export function calculateUserCompanyValues() {
  // user id
  store.userModel.userId = 'User_' + Date.now();

  // kpi percentages
  const kpiPercentages = store.userModel.kpiPercentages;
  let index = 0;

  for (const key of Object.keys(kpiPercentages)) {
    const kpiRange = store.maxKpiValues[index] - store.minKpiValues[index];
    const kpiPoint = Math.abs(store.userModel.kpiValues[key] - store.minKpiValues[index]);

    if (kpiRange > 0) {
      kpiPercentages[key] = kpiPoint / kpiRange;
    } else {
      kpiPercentages[key] = 0;
    }
    index++;
  }

  //  company and PPP values
  const planetAveragePercentage =
    kpiPercentages.co2 + kpiPercentages.energy + kpiPercentages.plastic;

  const peopleAveragePercentage =
    kpiPercentages.employeeHappiness + kpiPercentages.healthProgram + kpiPercentages.training;

  const profitAveragePercentage =
    kpiPercentages.revenue + kpiPercentages.innovation + kpiPercentages.customerSatisfaction;

  const pppSummPercentages =
    planetAveragePercentage + peopleAveragePercentage + profitAveragePercentage;

  const pppPercentages = {
    planet: {
      rounded: (planetAveragePercentage / pppSummPercentages).toFixed(2),
      original: planetAveragePercentage / pppSummPercentages,
    },
    people: {
      rounded: (peopleAveragePercentage / pppSummPercentages).toFixed(2),
      original: peopleAveragePercentage / pppSummPercentages,
    },
    profit: {
      rounded: (profitAveragePercentage / pppSummPercentages).toFixed(2),
      original: profitAveragePercentage / pppSummPercentages,
    },
  };

  // Check if rounded percentages are together 101%
  const is101 =
    Object.values(pppPercentages).reduce((acc, { rounded }) => acc + parseFloat(rounded), 0) > 1;

  // Check if rounded percentages are together 99%
  const is99 =
    Object.values(pppPercentages).reduce((acc, { rounded }) => acc + parseFloat(rounded), 0) < 1;

  if (is101 || is99) {
    const percentageEntries = Object.entries(pppPercentages);

    // Sort by fractional digits to determine which value can be rounded
    // in another way. Rounding issues happens when two (or more) values get
    // rounded up or down. To fix this situation, we round the lower (when "falsely" rounded up)
    // value down or the higher (when "falsely" rounded down) value up.
    percentageEntries.sort(([, { original: a }], [, { original: b }]) => {
      const fractionalDigitsA = a * 100 - Math.trunc(a * 100);
      const fractionalDigitsB = b * 100 - Math.trunc(b * 100);

      return fractionalDigitsA - fractionalDigitsB;
    });

    // This does only work for three values
    const affectedEntry = pppPercentages[percentageEntries[1][0]];

    if (is101) affectedEntry.rounded = (Math.floor(affectedEntry.original * 100) / 100).toFixed(2);
    if (is99) affectedEntry.rounded = (Math.ceil(affectedEntry.original * 100) / 100).toFixed(2);
  }

  store.userModel.companyValue = (
    1 -
    Math.abs(0.33 - pppPercentages.planet.rounded) -
    Math.abs(0.33 - pppPercentages.people.rounded) -
    Math.abs(0.33 - pppPercentages.profit.rounded)
  ).toFixed(2);

  store.userModel.planetPercentage = pppPercentages.planet.rounded;
  store.userModel.peoplePercentage = pppPercentages.people.rounded;
  store.userModel.profitPercentage = pppPercentages.profit.rounded;
  store.userModel.kpiPercentagesMatrix = '';

  for (const key of Object.keys(kpiPercentages)) {
    store.userModel.kpiPercentagesMatrix += `${kpiPercentages[key].toFixed(2)},`;
  }
  store.userModel.companyColor = getPPPFocus().color;
}

export function getRadarChartData() {
  calculateUserCompanyValues();

  // radar chart
  const MIN_CHART_VALUE = 0.5;
  const PPP_SECTION_COUNT = 3;
  const { kpiPercentages } = store.userModel;
  const kpiRadarChartData = [];

  for (let i = 0; i <= PPP_SECTION_COUNT; i++) {
    let keyIndex = 0;
    const kpiSection = [];

    for (const key of Object.keys(kpiPercentages)) {
      let kpiValue = MIN_CHART_VALUE + store.userModel.kpiPercentages[key];

      if (i === 3 && keyIndex > 2) {
        kpiValue = 0;
      }
      if (i === 2 && (keyIndex < 3 || keyIndex > 5)) {
        kpiValue = 0;
      }
      if (i === 1 && keyIndex < 6) {
        kpiValue = 0;
      }

      const kpiObject = {
        axis: key,
        value: kpiValue,
      };

      kpiSection.push(kpiObject);
      keyIndex++;
    }

    kpiSection.reverse();

    kpiRadarChartData.push(kpiSection);
  }

  // eslint-disable-next-line no-console
  // console.log(kpiRadarChartData);

  return kpiRadarChartData;
}

export function getFocusDistributionFromUserModel(userModel) {
  let peoplePercentage;
  let planetPercentage;
  let profitPercentage;

  if (userModel.peoplePercentagesValue) {
    peoplePercentage = userModel.peoplePercentagesValue;
    planetPercentage = userModel.planetPercentagesValue;
    profitPercentage = userModel.profitPercentagesValue;
  }
  if (userModel.peoplePercentage) {
    peoplePercentage = userModel.peoplePercentage;
    planetPercentage = userModel.planetPercentage;
    profitPercentage = userModel.profitPercentage;
  }

  const focusDistribution = [
    {
      id: pppSections.PEOPLE.id,
      label: i18n.t(pppSections.PEOPLE.labelId),
      color: pppSections.PEOPLE.color,
      value: peoplePercentage,
    },
    {
      id: pppSections.PLANET.id,
      label: i18n.t(pppSections.PLANET.labelId),
      color: pppSections.PLANET.color,
      value: planetPercentage,
    },
    {
      id: pppSections.PROFIT.id,
      label: i18n.t(pppSections.PROFIT.labelId),
      color: pppSections.PROFIT.color,
      value: profitPercentage,
    },
  ];
  return focusDistribution;
}

export function getFocusDistribution() {
  return getFocusDistributionFromUserModel(store.userModel);
}

export function getPPPFocus() {
  const { userModel } = store;
  let pppFocusId = pppSections.PEOPLE.id;

  if (
    userModel.planetPercentage >= userModel.peoplePercentage &&
    userModel.planetPercentage >= userModel.profitPercentage
  ) {
    pppFocusId = pppSections.PLANET.id;
  }

  if (
    userModel.profitPercentage >= userModel.peoplePercentage &&
    userModel.profitPercentage >= userModel.planetPercentage
  ) {
    pppFocusId = pppSections.PROFIT.id;
  }

  return Object.values(pppSections).find(({ id }) => id === pppFocusId);
}

export function setDeferedInstallationPrompt(deferedInstallationPrompt) {
  store.deferedInstallationPrompt = deferedInstallationPrompt;
}

export function currentSection() {
  return store.questionSections[store.sectionIndex];
}

export function currentQuestion() {
  return currentSection().sectionQuestions[store.questionIndex];
}

export function questionCount() {
  return currentSection().sectionQuestions.length;
}
